import React from 'react';
import { Box, Button, Chip, CircularProgress, makeStyles, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  EmojiObjects,
  FiberManualRecord,
  FormatQuote,
  InfoOutlined,
  NewReleasesOutlined,
  Subject,
} from '@material-ui/icons';
import { ClientInsightStatus, IClientInsightModel } from './interfaces';

interface IProps {
  data?: IClientInsightModel;
  /** Идёт сборка профиля на бэке (обычно ~8–20 сек, изредка дольше). */
  loading?: boolean;
  /** Запрос профиля завершился ошибкой. */
  error?: boolean;
  /** Повторить запрос (показывается в блоке ошибки). */
  onRetry?: () => void;
}

const STATUS_CONFIG: Record<ClientInsightStatus, { label: string; bg: string; color: string }> = {
  red: { label: 'красный', bg: '#fde2e2', color: '#b3261e' },
  yellow: { label: 'жёлтый', bg: '#fdf3d3', color: '#9a7b15' },
  green: { label: 'зелёный', bg: '#d6f5e3', color: '#1a7f4b' },
};

const PHRASE_BG = '#eaf1fd';
const PHRASE_ACCENT = '#3a5bd9';

const useStyles = makeStyles((theme) => ({
  root: {
    border: '1px solid #e6e8ec',
    borderRadius: 12,
    backgroundColor: '#fff',
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  statusDot: {
    fontSize: 14,
    marginRight: 8,
  },
  name: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1a1d1f',
  },
  subtitle: {
    fontSize: 14,
    color: '#8a94a6',
    marginLeft: 6,
  },
  statusChip: {
    marginLeft: 'auto',
    fontWeight: 600,
  },
  accentBlock: {
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: theme.spacing(1.5),
    borderLeft: '3px solid',
  },
  blockHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 4,
  },
  blockIcon: {
    fontSize: 16,
    marginRight: 6,
  },
  blockTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  blockText: {
    fontSize: 14,
    color: '#1a1d1f',
    lineHeight: 1.4,
  },
  phraseText: {
    fontSize: 14,
    color: '#1a1d1f',
    lineHeight: 1.4,
    fontStyle: 'italic',
  },
  section: {
    marginTop: theme.spacing(2),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
    color: '#8a94a6',
  },
  sectionIcon: {
    fontSize: 16,
    marginRight: 6,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  reasonRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 6,
  },
  reasonText: {
    fontSize: 14,
    color: '#1a1d1f',
    marginRight: 8,
  },
  sourceChip: {
    marginRight: 6,
    marginTop: 2,
    marginBottom: 2,
    backgroundColor: '#eef0f3',
    color: '#3f4754',
  },
  whatsNewBlock: {
    backgroundColor: '#f4f5f7',
    borderRadius: 8,
    padding: '10px 14px',
  },
  stateRow: {
    display: 'flex',
    alignItems: 'center',
  },
  stateText: {
    fontSize: 14,
    color: '#8a94a6',
    marginLeft: 10,
  },
  errorText: {
    fontSize: 14,
    color: '#b3261e',
  },
}));

/**
 * Карточка-подсказка оператору с информацией о клиенте «из другой системы»:
 * статус-светофор, рекомендуемое действие, готовая фраза, причины статуса с
 * источниками и что нового с прошлого контакта. Данные приходят с бэка
 * (POST /ai/client-profile); статус-светофор выводится эвристикой на фронте.
 */
const ClientInsightCard: React.FC<IProps> = ({ data, loading, error, onRetry }) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.stateRow}>
          <CircularProgress size={18} />
          <Typography className={classes.stateText}>Формируется ИИ-подсказка о клиенте…</Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="rect" height={60} style={{ borderRadius: 8, marginBottom: 12 }} />
          <Skeleton variant="rect" height={60} style={{ borderRadius: 8, marginBottom: 12 }} />
          <Skeleton width="70%" />
          <Skeleton width="55%" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <Box className={classes.stateRow} style={{ justifyContent: 'space-between' }}>
          <Typography className={classes.errorText}>Не удалось сформировать ИИ-подсказку о клиенте.</Typography>
          {onRetry && (
            <Button size="small" color="primary" onClick={onRetry}>
              Повторить
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box className={classes.root}>
        <Typography variant="body2" color="textSecondary">
          Нет данных по клиенту
        </Typography>
      </Box>
    );
  }

  const status = STATUS_CONFIG[data.status];

  return (
    <Box className={classes.root}>
      {/* Шапка */}
      <Box className={classes.header}>
        <FiberManualRecord className={classes.statusDot} style={{ color: status.color }} />
        <Typography className={classes.name} component="span">
          {data.clientName}
        </Typography>
        {data.subtitle && (
          <Typography className={classes.subtitle} component="span">
            · {data.subtitle}
          </Typography>
        )}
        <Chip
          size="small"
          className={classes.statusChip}
          style={{ backgroundColor: status.bg, color: status.color }}
          label={status.label}
        />
      </Box>

      {/* Кратко о клиенте (саммари — показывается в менеджерском виде) */}
      {data.summary && (
        <Box className={classes.section} style={{ marginTop: 0, marginBottom: 12 }}>
          <Box className={classes.sectionHeader}>
            <Subject className={classes.sectionIcon} />
            <Typography className={classes.sectionTitle}>Кратко о клиенте</Typography>
          </Box>
          <Box className={classes.whatsNewBlock}>
            <Typography className={classes.blockText}>{data.summary}</Typography>
          </Box>
        </Box>
      )}

      {/* Рекомендуемое действие */}
      {data.recommendedAction && (
        <Box className={classes.accentBlock} style={{ backgroundColor: status.bg, borderLeftColor: status.color }}>
          <Box className={classes.blockHeader} style={{ color: status.color }}>
            <EmojiObjects className={classes.blockIcon} />
            <Typography className={classes.blockTitle}>Рекомендуемое действие</Typography>
          </Box>
          <Typography className={classes.blockText}>{data.recommendedAction}</Typography>
        </Box>
      )}

      {/* Готовая фраза для открытия */}
      {data.openingPhrase && (
        <Box className={classes.accentBlock} style={{ backgroundColor: PHRASE_BG, borderLeftColor: PHRASE_ACCENT }}>
          <Box className={classes.blockHeader} style={{ color: PHRASE_ACCENT }}>
            <FormatQuote className={classes.blockIcon} />
            <Typography className={classes.blockTitle}>Готовая фраза для открытия</Typography>
          </Box>
          <Typography className={classes.phraseText}>«{data.openingPhrase}»</Typography>
        </Box>
      )}

      {/* Почему {статус} */}
      {data.reasons.length > 0 && (
        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>
            <InfoOutlined className={classes.sectionIcon} />
            <Typography className={classes.sectionTitle}>Почему {status.label}</Typography>
          </Box>
          {data.reasons.map((reason) => (
            <Box className={classes.reasonRow} key={reason.text}>
              <Typography className={classes.reasonText} component="span">
                {reason.text}
              </Typography>
              {(reason.sources || []).map((source) => (
                <Chip size="small" className={classes.sourceChip} label={source} key={source} />
              ))}
            </Box>
          ))}
        </Box>
      )}

      {/* Что нового с прошлого контакта */}
      {data.whatsNew && (
        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>
            <NewReleasesOutlined className={classes.sectionIcon} />
            <Typography className={classes.sectionTitle}>Что нового с прошлого контакта</Typography>
          </Box>
          <Box className={classes.whatsNewBlock}>
            <Typography className={classes.blockText}>{data.whatsNew}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ClientInsightCard;
